import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, RouterLink} from '@angular/router';
import {filter, first, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {AuthenticationService} from '../../service/authentication.service';
import * as fromRoot from '../../store/app.reducers';
import {Store} from '@ngrx/store';
import {SystemSettingsService} from '../../service/system-settings.service';
import {UserService} from '../../service/user.service';
import {NotificationSignalStore} from '../../store/notification/notification.signal-store';
import {TranslatePipe} from '@ngx-translate/core';
import {PopoverDirective} from 'ngx-bootstrap/popover';
import {ucsIsNil} from '../../misc/utils';
import {SpinnerService} from '../../service/spinner.service';
import {NgxFloatUiContentComponent, NgxFloatUiDirective, NgxFloatUiPlacements} from 'ngx-float-ui';
import {UserSignalStore} from '../../store/user/user.signal-store';
import {MaintenanceSignalStore} from '../../store/maintenance/maintenance.signal-store';
import {
  NotificationComponent
} from '../../../administration/system-settings/user-notifications/notification/notification.component';
import {IconComponent} from '../icon/icon.component';
import {NgClass} from '@angular/common';
import {NgSelectComponent} from '@ng-select/ng-select';
import {FormsModule} from '@angular/forms';
import {OpenTasksComponent} from '../open-tasks/open-tasks.component';
import {ExpiringOfferCalendarComponent} from '../calendar/expiring-offer-calendar/expiring-offer-calendar.component';
import {TruncatePipe} from '../../pipe/truncate.pipe';
import {MaintenanceVehicleSignalStore} from '../../../maintenance/vehicle/maintenance-vehicle.signal-store';
import {SupportDevToolComponent} from '../support-dev-tool/support-dev-tool.component';

@Component({
  selector: 'ucs-top-menu',
  templateUrl: './top-menu.component.html',
  styleUrls: ['./top-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NotificationComponent,
    RouterLink,
    IconComponent,
    NgClass,
    NgxFloatUiDirective,
    NgxFloatUiContentComponent,
    NgSelectComponent,
    FormsModule,
    OpenTasksComponent,
    ExpiringOfferCalendarComponent,
    TranslatePipe,
    TruncatePipe,
    SupportDevToolComponent
  ]
})
export class TopMenuComponent implements OnDestroy, OnInit {
  @ViewChild('pop', {static: false}) pop: PopoverDirective;
  currentRoute: string;
  administrationRoute: string;
  userState: any;
  userInfo: UserInfoDto;
  selectedDealerId: number;
  currentDealer: DealerBaseDto;
  showMobileUserMenu = false;
  showSubMenuAdministration: boolean;
  showSubMenuOffer: boolean;
  showSubMenuSell: boolean;
  showUserNotifications = false;
  appScope: AppScope;
  numberOfOpenTasks = computed(() => this.maintenanceSignalStore.openTasks().length);
  isAllowedToSeeOpenTasks = false;
  readonly userSignalStore = inject(UserSignalStore);
  readonly maintenanceVehicleSignalStore = inject(MaintenanceVehicleSignalStore);
  isAllowedSeeDealerDocuments: boolean;
  isAllowedSeePremiumDealers = computed(() =>
    this.userSignalStore.userInfo() && this.userSignalStore.isFeatureActivatedForAnyChannel('UPDATE_PREMIUM_CUSTOMER')
  );
  // can sell offers in one of the channels
  isSeller = false;
  // can buy offers in one of the channels
  isBuyer = false;
  // is allowed to see admin area
  isAdmin = false;
  isAllowedToSeeSettings = false;
  // states whether the user's current dealer is not locked
  isNotLocked = false;
  // states whether the current user has not accepted the terms and conditions
  needGtcApproval = false;
  // states whether the current user is allowed to see the offer maintenance section. PIA examiner, e.g., is not allowed
  isAllowedToSeeOfferMaintenance = false;
  isAllowedToSeeDealerLocking = false;
  // show dropdown change local
  isShowLocalSwitchFeatureActive = false;
  // is allowed to see enforced auction areas
  isEnforcedAuctionUser = false;
  isEnforcedAuctionToggleEnabled: boolean;
  isEnforcedAuctionSettingEnabled: boolean;
  isSpinnerActive: boolean = false;
  actualLocale: string;
  showReportMenu: boolean = false;
  // all available locals
  languages: { value: string; name: string }[] = [
    {value: 'en', name: 'top-menu.dropdownLocal.en'},
    {value: 'de', name: 'top-menu.dropdownLocal.de'},
    {value: 'sl', name: 'top-menu.dropdownLocal.sl'},
    {value: 'bs', name: 'top-menu.dropdownLocal.bs'},
    {value: 'bg', name: 'top-menu.dropdownLocal.bg'},
    {value: 'hr', name: 'top-menu.dropdownLocal.hr'},
    {value: 'cs', name: 'top-menu.dropdownLocal.cs'},
    {value: 'fr', name: 'top-menu.dropdownLocal.fr'},
    {value: 'hu', name: 'top-menu.dropdownLocal.hu'},
    {value: 'it', name: 'top-menu.dropdownLocal.it'},
    {value: 'mk', name: 'top-menu.dropdownLocal.mk'},
    {value: 'pl', name: 'top-menu.dropdownLocal.pl'},
    {value: 'pt', name: 'top-menu.dropdownLocal.pt'},
    {value: 'ro', name: 'top-menu.dropdownLocal.ro'},
    {value: 'sr', name: 'top-menu.dropdownLocal.sr'},
    {value: 'sk', name: 'top-menu.dropdownLocal.sk'},
    {value: 'es', name: 'top-menu.dropdownLocal.es'},
    {value: 'uk', name: 'top-menu.dropdownLocal.uk'}
  ];
  readonly maintenanceSignalStore = inject(MaintenanceSignalStore);
  isCalenderActive = computed(() =>
    this.userSignalStore.userInfo()
    && this.maintenanceVehicleSignalStore.pageSettings().channel
    && (this.userSignalStore.featureSettingForChannel('VEHICLE_MAINTENANCE.expiring_auctions_calendar', this.maintenanceVehicleSignalStore.pageSettings().channel) === 'true')
  );
  protected readonly NgxFloatUiPlacements = NgxFloatUiPlacements;
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authService = inject(AuthenticationService);
  private readonly systemSettingsService = inject(SystemSettingsService);
  private readonly store = inject<Store<fromRoot.AppState>>(Store);
  private readonly notificationSignalStore = inject(NotificationSignalStore);
  private readonly spinnerService = inject(SpinnerService);
  private readonly unsubscribe: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    document.getElementById('ucs-content').addEventListener('click', this.hideBurgerMenuContent);

    this.store.select(fromRoot.getUserInfo)
      .pipe(takeUntil(this.unsubscribe),
        filter(userInfo => (!!userInfo && !!(userInfo.firstName)))
      )
      .subscribe(userInfo => {
        this.userInfo = userInfo;
        this.userInfo.dealers = this.preprocessDealers(userInfo.dealers);
        this.selectedDealerId = userInfo.currentDealerId;
        this.currentDealer = this.userInfo.dealers.find(dealer => dealer.id === userInfo.currentDealerId);

        this.appScope = 'SALES_STORE';

        this.router.events
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(event => { // subscribe to change of route parameters
            if (event instanceof NavigationEnd) {
              this.currentRoute = event.url;
              if (this.currentRoute.startsWith('/maintenance')) {
                this.showSubMenuAdministration = false;
                this.showSubMenuOffer = false;
                this.showSubMenuSell = true;
              } else if (this.currentRoute.startsWith('/administration')) {
                this.showSubMenuAdministration = true;
                this.showSubMenuOffer = false;
                this.showSubMenuSell = false;
              } else {
                this.showSubMenuAdministration = false;
                this.showSubMenuSell = false;
                this.showSubMenuOffer = true;
              }
            }
          });

        this.systemSettingsService
          .isSystemFeatureActivatedForAnyChannel('OPEN_TASK_LIST').pipe(first())
          .subscribe(isAllowedToSeeOpenTasks => {
            this.isAllowedToSeeOpenTasks = isAllowedToSeeOpenTasks;
            if (isAllowedToSeeOpenTasks) {
              this.maintenanceSignalStore.fetchOpenTasks();
            }
          });

        this.systemSettingsService
          .isSystemFeatureActivatedForAnyChannel('CONTRACT_PARTNER_LOCKING').pipe(first())
          .subscribe(isAllowedToSeeDealerLocking => {
            this.isAllowedToSeeDealerLocking = isAllowedToSeeDealerLocking;
          });

        this.systemSettingsService
          .isSystemFeatureActivatedForAnyChannel('SHOW_DROPDOWN_LOCAL_CHANGE')
          .subscribe(isShowLocalSwitchFeatureActive => {
            this.actualLocale = 'en';
            this.isShowLocalSwitchFeatureActive = isShowLocalSwitchFeatureActive;
          });

        this.systemSettingsService
          .isSystemFeatureActivatedForAnyChannel('RECIEVE_USER_NOTIFICATIONS')
          .subscribe(isShowUserNotificationFeatureActive => {
            this.showUserNotifications = isShowUserNotificationFeatureActive;
          })
        ;

        this.systemSettingsService
          .isSystemFeatureActivatedForAnyChannel('ENFORCED_AUCTION')
          .subscribe(isEnabled => {
            this.isEnforcedAuctionToggleEnabled = isEnabled;
          });

        this.systemSettingsService.getSystemFeatureSettingForChannel(
          'ENFORCED_AUCTION.sales', 'PIA')
          .pipe(takeUntil(this.unsubscribe)).subscribe(settingString => {
            if (settingString) {
              const setting = JSON.parse(settingString);
              this.isEnforcedAuctionSettingEnabled = setting.enabled;
            }
          });

        this.systemSettingsService
          .isSystemFeatureActivatedForAnyChannel('REPORTING').pipe(first())
          .subscribe(showReports => {
            this.showReportMenu = showReports;
          });

        this.getUserState();
      });
  }


  dealerChange(dealer: any) {

    if (ucsIsNil(dealer)) {
      return;
    }

    this.authService.setDealer(dealer.id.toString()).pipe(takeUntil(this.unsubscribe)).subscribe((token) => {
      this.authService.storeToken(token);
      this.userSignalStore.fetchUserInfo();
      this.store.select(fromRoot.getUserInfo)
        .pipe(
          filter(u => (u?.currentDealerId + '') === dealer.id.toString()),
          first()
        )
        .subscribe(userInfo => {
          this.appScope = 'SALES_STORE';
          if (this.isSeller) {
            this.navigateSeller(userInfo);
          } else {
            if (!userInfo.locked) {
              this.router.navigate(['/maintenance/vehicle']);
            }
            this.appScope = 'SALES_MAINTENANCE';
          }
          this.notificationSignalStore.fetchUserNotifications(this.appScope);
        });
      location.reload();
    });
  }

  getCompanyNumberOrPbvCustomerNumber(dealer: DealerBaseDto): string {
    // case 1: the dealer has a regular company number (not just the country code, e.g. 'AT')
    if (dealer?.companyNumber !== this.userInfo?.country) {
      return dealer.companyNumber;

      // case 2: the dealer has a PBV customer number AND has roles for the PB channel
    } else if (this.userState?.hasAnyRoleForPb && dealer?.pbvCustomerNumber) {
      return dealer.pbvCustomerNumber;
    }

    // case 3: the dealer has neither -> return companyNumber as is (will be just the country code, e.g. 'AT')
    return dealer?.companyNumber ?? '';
  }

  /**
   * programmatically hides BurgerMenuContent
   */
  hideBurgerMenuContent() {
    document.getElementById('burger-menu-toggle').setAttribute('aria-expanded', 'false');
    document.getElementById('burger-menu-toggle').classList.add('collapsed');
    document.getElementById('burger-menu-content').classList.remove('show');
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    document.getElementById('ucs-content').removeEventListener('click', this.hideBurgerMenuContent);
  }

  subMenuOffer() {
    this.showSubMenuOffer = true;
    this.showSubMenuSell = false;
    if (this.showUserNotifications) {
      this.initSalesStoreNotifications();
    }
  }

  subMenuSell() {
    this.showSubMenuSell = true;
    this.showSubMenuOffer = false;
    if (this.showUserNotifications) {
      this.initMaintenanceNotifications();
    }
  }

  changeLocal(locale: string) {
    this.actualLocale = locale;
    this.userSignalStore.updateUserLocale(locale);
    if (this.actualLocale !== 'de-de-key') {
      localStorage.setItem(UserService.CURRENT_LANGUAGE + this.userInfo.id, this.actualLocale);
    }

    // refresh component
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'preserve',
      fragment: this.activatedRoute.snapshot.fragment
    });
  }

  isInSalesScope(): boolean {
    return this.currentRoute === '/' ||
        ['/offer', '/saved-search', '/bookmarks', '/my-auctions', '/finished-offers', '/pia-auction'].some(substr => this.currentRoute?.startsWith(substr));
  }

  initMaintenanceNotifications() {
    this.notificationSignalStore.fetchUserNotifications('SALES_MAINTENANCE');
  }

  initSalesStoreNotifications() {
    this.notificationSignalStore.fetchUserNotifications('SALES_STORE');
  }

  onOpenTaskNavItemClick() {
    if (this.isAllowedToSeeOpenTasks) {
      this.maintenanceSignalStore.fetchOpenTasks();
      this.spinnerService.activateSpinner();
      this.isSpinnerActive = true;
    }
  }

  private getUserState() {
    this.store.select(fromRoot.getUserState)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(userState => {
        if (userState) {
          this.userState = userState;
          if (localStorage.getItem(UserService.CURRENT_LANGUAGE + this.userState.userInfo.id) !== null) {
            this.actualLocale = localStorage.getItem(UserService.CURRENT_LANGUAGE + this.userState.userInfo.id);
            this.userSignalStore.updateUserLocale(this.actualLocale);
          } else if (this.userSignalStore.locale()) {
            this.actualLocale = this.userSignalStore.locale();
          } else {
            this.actualLocale = 'en';
            this.userSignalStore.updateUserLocale(this.actualLocale);
          }
        }

        this.isBuyer = this.evaluateIsBuyer();
        this.isSeller = this.evaluateIsSeller();
        this.isAdmin = this.evaluateIsAdmin();
        this.isEnforcedAuctionUser = this.userState.isBuyerEnforcedAuction;
        this.isAllowedToSeeSettings = this.evaluateIsAllowedToSeeSettings();
        this.isNotLocked = !AuthenticationService.isOnlyAllowedToSeeFinishedOffersReasonLocked(this.userInfo);
        // Enforced auction viewer has the same permission as missing GTC
        this.needGtcApproval = AuthenticationService.isOnlyAllowedToSeeFinishedOffersReasonGtc(this.userInfo) ||
              (userState.isViewerEnforcedAuction && !userState.hasAnyBuyerRoleOrAdmin);
        this.isAllowedSeeDealerDocuments = userState.isAdminPia || userState.isDevPoi;
        this.isAllowedToSeeOfferMaintenance = this.evaluateIsAllowedToSeeOfferMaintenance();

        if (this.isAllowedToSeeSettings) {
          this.administrationRoute = 'settings';
        } else {
          this.administrationRoute = 'reporting';
        }
      });
  }

  private preprocessDealers(dealers: DealerBaseDto[]): any[] {
    return dealers.map(dealer => ({
      ...dealer,
      displayName: `${this.getCompanyNumberOrPbvCustomerNumber(dealer)} ${dealer.name}`
    })).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  private evaluateIsAllowedToSeeOfferMaintenance(): boolean {
    return AuthenticationService.canSellOffersForAnyChannel(this.userState)
        || this.userState.isCustodianPb
        || this.userState.isClerkPb;
  }

  private evaluateIsAdmin(): boolean {
    return AuthenticationService.isAdminForAnyChannel(this.userState) ||
        AuthenticationService.isSupportOrDevPoi(this.userState) ||
        this.userState.isSupportPb;
  }

  private evaluateIsSeller(): boolean {
    return AuthenticationService.canSellOffersForAnyChannel(this.userState)
        || this.userState.isExaminerPia
        || this.userState.isCustodianPb
        || this.userState.isSurveyorPb
        || this.userState.isClerkPb
        || this.userState.isClerkBmiPb;
  }

  private evaluateIsBuyer(): boolean {
    return AuthenticationService.canBuyOffers('PIA', this.userState)
        || AuthenticationService.canBuyOffers('ALL_UC', this.userState)
        || AuthenticationService.canBuyOffers('PB', this.userState)
        || AuthenticationService.canBuyOffers('DIN_BIL', this.userState)
        || AuthenticationService.canBuyOffers('VGRX', this.userState);
  }

  private evaluateIsAllowedToSeeSettings(): boolean {
    return this.userState.isAuctionSellerAllUc ||
        (this.userState.isAuctionSellerPia && !this.userState.isSellerTradeInPia) ||
        this.userState.isAuctionSellerPb ||
        this.userState.isAuctionSellerDinBil ||
        this.userState.isAuctionSellerVgrx ||
        this.userState.isBuyNowSellerAllUc ||
        this.userState.isBuyNowSellerPia ||
        this.userState.isBuyNowSellerPb ||
        this.userState.isBuyNowSellerDinBil ||
        this.userState.isBuyNowSellerVgrx ||
        this.userState.isBuyerAllUc ||
        this.userState.isBuyerPia ||
        this.userState.isBuyerPb ||
        this.userState.isBuyerDinBil ||
        this.userState.isBuyerVgrx ||
        this.userState.isCustodianPb ||
        this.userState.isAdminPb ||
        this.userState.isAdminAllUc ||
        this.userState.isAdminDinBil ||
        this.userState.isAdminVgrx ||
        this.userState.isAdminPia ||
        this.userState.isDevPoi ||
        this.userState.isSupportPoi ||
        this.userState.isSupportPb ||
        this.userState.isClerkPb ||
        this.userState.isViewerEnforcedAuction;
  }

  private navigateSeller(userInfo: UserInfoDto) {
    if (userInfo.locked) {
      this.router.navigate(['/finished-offers']);
    } else {
      this.router.navigate(['/']);
    }
  }
}
